import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, User, Tag, Share2, Languages, Sparkles } from 'lucide-react';
import RelatedTools from '../components/RelatedTools';
import RelatedBlogs from '../components/RelatedBlogs';
import TopicalCrossLinks from '../components/TopicalCrossLinks';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import AuthorBox from '../components/AuthorBox';
import { safeHtml } from '../lib/sanitize';
import { renderBlogContent } from '../lib/renderBlogContent';
import '../styles/blog-content.css';  // Permanent .virat-blog theme — replaces the inline <style> block that used to ship inside each blog's HTML body.
import PriyaRecommendationCard, { isPriyaRecommendableBlog } from '../components/PriyaRecommendationCard';
import TableOfContents from '../components/TableOfContents';
import { useLang } from '../context/LanguageContext';
import {
  articleSchema,
} from '../utils/schemas';
import { trackWhatsAppClick, trackConsultationCTAClick, trackPoochoGurujiCTAClick } from '../utils/analytics';
import { attachPoochoGurujiCTATracker } from '../lib/poochoGurujiCTA';
import { fetchWithRetry, readCache, writeCache } from '../lib/fetchWithRetry';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { lang } = useLang();
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  // Blog-content DOM ref for attaching the Poocho Guruji CTA click tracker
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlog();
    if (slug) {
      axios.post(`${API}/api/blogs/${slug}/view`).catch(() => {});
    }
  }, [slug]);

  // Attach GA4 click tracker to the embedded Poocho Guruji CTA button.
  // Re-binds whenever blog / translation swap changes the rendered HTML.
  useEffect(() => {
    if (!contentRef.current || !blog) return undefined;
    return attachPoochoGurujiCTATracker(
      contentRef.current,
      blog.slug || slug,
      trackPoochoGurujiCTAClick,
    );
  }, [blog, translation, slug]);

  // Auto-translate when language switches (both directions)
  useEffect(() => {
    if (blog && !translating) {
      const hindiChars = ((blog.title || '') + (blog.content || '')).match(/[\u0900-\u097F]/g) || [];
      const totalChars = ((blog.title || '') + (blog.content || '')).replace(/\s/g, '').length || 1;
      const blogIsHindi = hindiChars.length / totalChars > 0.3;
      
      // Hindi blog + English selected → translate to English
      if (blogIsHindi && lang === 'en' && !translation) {
        translateBlog('en');
      }
      // English blog + Hindi selected → translate to Hindi  
      if (!blogIsHindi && lang === 'hi' && !translation) {
        translateBlog('hi');
      }
    }
  }, [blog, lang]);

  // Reset translation when language changes back to original
  useEffect(() => {
    setTranslation(null);
  }, [lang]);

  const translateBlog = async (targetLang) => {
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/api/blogs/translate`, { blog_id: blog.id, target_lang: targetLang });
      setTranslation(res.data);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setTranslating(false);
    }
  };

  const getTitle = () => translation?.title || blog?.title;
  const getExcerpt = () => translation?.excerpt || blog?.excerpt;
  const getContent = () => translation?.content || blog?.content;

  const fetchBlog = async () => {
    // Feb 24, 2026 — resilient fetch with 3× exponential-backoff retry +
    // localStorage cache. Prevents the false "Blog not found" that used
    // to appear on transient 5xx / network blips during deploy.
    // Instant optimistic paint from cache (if any).
    const cached = readCache(`blog-detail:${slug}`);
    if (cached && cached.data && cached.data.slug === slug) {
      setBlog(cached.data);
      setLoading(false);
    }
    try {
      const response = await fetchWithRetry(
        () => axios.get(`${API}/api/blogs/${slug}`, { timeout: 15000 }),
        { attempts: 3, baseDelayMs: 500 }
      );
      // Backend signals a slug-migration redirect for SEO continuity
      if (response.data?.redirect && response.data.redirect !== slug) {
        navigate(`/blog/${response.data.redirect}`, { replace: true });
        return;
      }
      setBlog(response.data);
      writeCache(`blog-detail:${slug}`, response.data);
      setError('');
      // Track blog view (best-effort, non-blocking)
      try {
        fetch(`${API}/api/track/event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'blog_view',
            source: 'blog_detail',
            blog_slug: response.data.slug,
            category: response.data.category,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch { /* noop */ }
    } catch (error) {
      // A real 404 is a true "not found" — mark the page accordingly.
      // Any other failure after 3 retries + no cache → surface a soft
      // retry UI instead of a hard "Blog not found" page.
      const status = error?.response?.status;
      if (status === 404) {
        setError('Blog not found.');
      } else if (!(cached && cached.data && cached.data.slug === slug)) {
        setError('connection');  // sentinel — UI distinguishes this
      }
      // If we already painted from cache, keep showing it silently.
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-muted">Loading...</div>
      </div>
    );
  }

  if (error || !blog) {
    const isConnectionError = error === 'connection';
    return (
      <div className="min-h-screen bg-background">
        <SEO
          title={isConnectionError ? 'Loading… | ViratAstro' : 'Blog Not Found | ViratAstro'}
          description="The blog post you are looking for could not be found."
          path="/blog/not-found"
          noIndex
        />
        {/* Feb 12, 2026 SEO Fix — only emit the hard 404 signal for a true
            missing-blog response, NOT for transient connection failures. */}
        {!isConnectionError && (
          <Helmet>
            <meta name="prerender-status-code" content="404" />
          </Helmet>
        )}
        <Header />
        <div className="pt-32 text-center" data-testid={isConnectionError ? "blog-detail-conn-error" : "blog-detail-not-found"}>
          {isConnectionError ? (
            <>
              <h1 className="font-heading text-4xl text-text-main mb-4">कनेक्शन में दिक्कत</h1>
              <p className="text-text-muted mb-6">सर्वर से जवाब नहीं मिला। कृपया पुनः प्रयास करें।</p>
              <button
                onClick={() => { setError(''); setLoading(true); fetchBlog(); }}
                className="px-6 py-2 rounded-full bg-gold/20 text-gold border border-gold/30 hover:bg-gold/30 transition-colors text-sm font-medium"
                data-testid="blog-detail-retry"
              >
                पुनः प्रयास करें
              </button>
            </>
          ) : (
            <>
              <h1 className="font-heading text-4xl text-text-main mb-4">{error || 'Blog not found'}</h1>
              <p className="text-text-muted mb-6">यह Blog Post उपलब्ध नहीं है। कृपया हमारे अन्य लेख पढ़ें।</p>
              <Link to="/blog" className="text-primary hover:underline">← सभी Blogs देखें</Link>
            </>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={blog.meta_title || getTitle()}
        description={blog.meta_description || getExcerpt() || `${getTitle()} - ज्योतिष ब्लॉग by Vivek Mudgal Astrologer`}
        path={`/blog/${blog.slug}`}
        image={blog.image_url && blog.image_url.startsWith('http') ? blog.image_url : (blog.image_url ? `https://viratastro.cloud${blog.image_url}` : undefined)}
        type="article"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: getTitle(), path: `/blog/${blog.slug}` },
        ]}
        keywords={`${blog.category}, ${getTitle()}, Vivek Virat Mudgal, ViratAstro, Vedic Astrology, Parashari Jyotish, ज्योतिष`}
        jsonLd={[
          // Feb 12, 2026 SEO Fix #2 — Site-wide schemas (Person, Organization,
          // WebSite, LocalBusiness) are now provided ONCE by the static
          // `public/index.html` baseline (visible pre-hydration to crawlers).
          // Previously they were re-injected at runtime causing each schema
          // type to appear TWICE per blog page in JSON-LD. Now the runtime
          // only emits PAGE-SPECIFIC schemas: Article + BreadcrumbList +
          // any auto-blog LLM-generated schemas (FAQPage etc).
          articleSchema({
            ...blog,
            title: getTitle(),
            excerpt: getExcerpt(),
            language: ((blog.title || '') + (blog.content || '')).match(/[\u0900-\u097F]/g) ? 'hi-IN' : 'en-IN',
          }),
          // 📎 Auto-blog LLM-generated schemas (FAQPage). Article type is
          // filtered out here because articleSchema() above is the canonical
          // Article for this page (properly linked to author @id).
          ...(Array.isArray(blog.jsonld) ? blog.jsonld : [])
            .filter(s => {
              if (!s || typeof s !== 'object') return false;
              const t = s['@type'];
              // Drop any duplicate Article/BlogPosting to prevent 2× Article on page
              if (t === 'Article' || t === 'BlogPosting') return false;
              return true;
            }),
        ]}
      />
      <Header />
      <FloatingActions />
      
      {/* Hero */}
      <section className="pt-32 pb-8 cosmic-bg">
        <div className="container-custom">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-8"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted mb-6">
            <span className="flex items-center gap-2">
              <img 
                src="/media/founder/IMG_9620-removebg-preview.png" 
                alt="Vivek Mudgal Astrologer" 
                className="w-8 h-8 rounded-full object-cover object-top border-2 border-gold/30"
              />
              <span>Vivek Mudgal Astrologer</span>
            </span>
            <span className="px-3 py-1 bg-primary text-white rounded-full flex items-center gap-1">
              <Tag className="w-3 h-3" /> {blog.category}
            </span>
            <span className="flex items-center gap-1" title="Published">
              <Calendar className="w-4 h-4" /> {formatDate(blog.created_at)}
            </span>
            {blog.updated_at && blog.updated_at !== blog.created_at && (
              <span className="flex items-center gap-1 text-gold/90 text-xs" title="Last reviewed & updated by Acharya Vivek जी" data-testid="blog-last-updated">
                <Sparkles className="w-3 h-3" /> Last updated {formatDate(blog.updated_at)}
              </span>
            )}
          </div>

          <h1 className="blog-hero-title mb-6" data-testid="blog-h1-title">
            {getTitle()}
            {translating && <span className="block text-base text-gold/60 mt-2 animate-pulse">{lang === 'en' ? 'Translating...' : 'अनुवाद हो रहा है...'}</span>}
          </h1>
          <p className="text-xl text-text-muted max-w-3xl leading-relaxed" style={{ overflowWrap: 'anywhere' }}>{getExcerpt()}</p>
        </div>
      </section>

      {/* Featured Image */}
      {blog.image_url && (
        <section className="pb-12">
          <div className="container-custom">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={blog.image_url} alt={blog.title} className="w-full h-96 md:h-[500px] object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="pb-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Auto Table of Contents — only renders for long articles (≥1500 words or ≥3 H2s) */}
            <TableOfContents contentSelector=".blog-content" />

            <div 
              ref={contentRef}
              className="prose prose-lg prose-invert max-w-none text-text-muted blog-content"
              dangerouslySetInnerHTML={safeHtml(renderBlogContent(getContent()))}
            />

            {/* Author Box — strengthens EEAT on every blog */}
            <AuthorBox />

            {/* Priya Khurana Recommendation — only on Tarot / Love / Relationship / Marriage / Compatibility / Emotional Healing blogs */}
            {isPriyaRecommendableBlog(blog) && <PriyaRecommendationCard />}

            {/* Feb 22, 2026 — Related Blogs (semantic, deterministic, no LLM) */}
            <RelatedBlogs blogId={blog.id} currentSlug={blog.slug} />

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-primary/10">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <p className="text-text-muted">Share this article</p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => {
                      const shareUrl = `${API}/api/share/blog/${slug}`;
                      const waText = `*${blog.title}*\n\n${blog.excerpt?.substring(0, 100)}...\n\n📖 Read: ${shareUrl}\n\n🔮 _ViratAstro - Vivek Mudgal Astrologer_\n🌐 viratastro.cloud`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, '_blank');
                    }}
                    className="px-4 py-2 rounded-full bg-green-600 text-white flex items-center gap-2 hover:bg-green-700 transition-all text-sm font-medium"
                    data-testid="share-whatsapp-btn"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </button>
                  <button 
                    onClick={() => {
                      const shareUrl = `${API}/api/share/blog/${slug}`;
                      navigator.clipboard?.writeText(shareUrl);
                      alert('Link copied!');
                    }}
                    className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all"
                    data-testid="share-btn"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* CTA — WhatsApp-first article-end conversion block */}
            <div className="mt-12 rounded-3xl overflow-hidden border border-gold/30 bg-gradient-to-br from-[#15093A] via-[#1f0d52] to-[#0f0508] p-8 sm:p-10 text-center relative" data-testid="blog-end-cta">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-gold/15 rounded-full blur-[100px]" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-purple-500/15 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <p className="text-xs tracking-[0.3em] uppercase text-gold/80 font-semibold mb-3">
                  Need personal guidance?
                </p>
                <h4 className="font-heading text-2xl sm:text-3xl text-text-main mb-3">
                  Talk to <span className="text-gold-gradient">Acharya Vivek जी</span> directly
                </h4>
                <p className="text-sm sm:text-base text-text-muted max-w-xl mx-auto mb-6">
                  33+ years of Vedic experience. Featured on Zee News, Aaj Tak, ABP News, News18.
                  WhatsApp Acharya जी directly or book a personal consultation.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="https://wa.me/919311369729?text=Namaste%20Acharya%20ji,%20I%20just%20read%20your%20blog%20and%20need%20guidance."
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick('blog_article_end', { blog_slug: slug })}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition shadow-lg shadow-green-900/30"
                    data-testid="blog-end-whatsapp-cta"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                    WhatsApp Acharya जी
                  </a>
                  <Link
                    to="/#book-consultation"
                    onClick={() => trackConsultationCTAClick('blog_article_end', { blog_slug: slug })}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gold text-bg-primary font-semibold text-sm hover:bg-yellow-400 transition shadow-lg"
                    data-testid="blog-end-consult-cta"
                  >
                    <Sparkles className="w-4 h-4" /> Book Consultation
                  </Link>
                </div>
                <p className="text-[11px] text-text-muted/60 mt-5">
                  🔒 100% private · 🪔 33+ years experience · 🌍 14+ countries served
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedTools />
      {/* Feb 12, 2026 SEO Fix — Topical cross-links from Blog → FAQ/Upay for
          topical cluster strengthening + AEO impression distribution (audit
          findings #9, #13). */}
      <TopicalCrossLinks hint={`${blog.category || ''} ${blog.slug || ''}`} lang={lang} />
      <Footer />
    </div>
  );
}
